import React, { useState, useEffect, useRef,useCallback } from "react";
import { apiCallGeneric } from "../../services/api-service";
import { ChevronDown } from "lucide-react";
import { sanitizeInput } from "./security-utils";
import "./Util.css";

/**
 * SearchWithFreeText (Auto Free Suggestion) Component
 * A reusable dropdown component that supports free text search and API-based suggestions.
 * Hardened with VAPT security measures and optimized for performance.
 */
const SearchWithFreeText = ({
  onDropdownChange,
  onChangeSearch,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectIndex, setSelectIndex] = useState(null);

  const dropdownRef = useRef(null);

  // Styles
  const dropdownHeaderStyle = {
    height: "40px",
    padding: "10px",
    border: isOpen ? "1px solid #842234" : "1px solid #E5E5E5",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#FFFFFF",
    color: (selectedOption || selectedValue !== "") ? "#1E1E1E" : "#81878C",
    boxShadow: isOpen ? "0 0 0 2px rgba(132, 34, 52, 0.1)" : "none",
    transition: "all 0.2s ease-in-out",
  };

  const optionStyle = (option, index) => ({
    padding: "10px 12px",
    cursor: "pointer",
    background:
      (selectedOption && selectedOption.id === option.id) || index === selectIndex
        ? "#F9F5F6"
        : "#fff",
    color: "#1E1E1E",
    textAlign: "left",
    fontSize: "14px",
    borderBottom: "1px solid #F5F7FA",
  });

  const dropdownOptionsStyle = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    background: "#fff",
    zIndex: 1000,
    maxHeight: "200px",
    overflowY: "auto",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  };

  const inputStyle = {
    width: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#1E1E1E",
    fontSize: "14px",
    fontWeight: "500",
  };

  // Fetch options from API
  const fetchOptions = useCallback(async () => {
    if (!apiUrl) return;

    setLoading(true);
    setError(null);
    try {
      if (apiUrl) {
        const response = await apiCallGeneric("GET", apiUrl, null, {
          params: { term: searchTerm },
        });

        if (response.status === 200 && response.data.success) {
          setOptions(response.data.suggestionlist);
          setFilteredOptions(response.data.suggestionlist);
        } else {
          setOptions([]);
          setFilteredOptions([]);
        }
      }
    } catch (err) {
      setError("Failed to fetch options. Try again.");
      setOptions([]);
      setFilteredOptions([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, searchTerm]);

  // Handle outside click
  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Handle option selection
  const handleSelectOption = (option, index) => {
    // Set the search term to the selected option's label
    setSearchTerm(option.label || option.value || "");
    // Call parent's onChange handler
    onDropdownChange(option);
    setSelectIndex(index);
    setIsOpen(false);
  };

  // Effects
  useEffect(() => {
    if (selectedValue && typeof selectedValue === "object") {
      setSearchTerm(selectedValue.label || "");
    } else if (typeof selectedValue === "string") {
      setSearchTerm(selectedValue);
    } else {
      setSearchTerm("");
    }
  }, [selectedValue]);

  // Debounced fetch and click outside registration
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    let timeoutId;
    if (isOpen) {
      if (searchTerm === "") {
        fetchOptions();
      } else {
        timeoutId = setTimeout(fetchOptions, 300);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [searchTerm, isOpen, fetchOptions, handleClickOutside]);

  // JSX
  return (
    <div ref={dropdownRef} style={{ position: "relative", height: "40px" }}>
      {/* Dropdown Header */}
      <div onClick={toggleDropdown} style={dropdownHeaderStyle}>
        <input
          type="text"
          value={searchTerm}
          autoComplete="off"
          onChange={(e) => {
            const newValue = e.target.value;
            setSearchTerm(newValue);
            onChangeSearch(newValue);
          }}
          placeholder={placeholder || "Search..."}
          className="dropdown-input"
          style={inputStyle}
          onClick={(e) => e.stopPropagation()}
        />
        <div style={{ display: "flex", alignItems: "center" }}>
          <ChevronDown size={20} color={isOpen ? "#842234" : "#81878C"} />
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div style={dropdownOptionsStyle}>
          {loading ? (
            <div style={{ padding: "12px", textAlign: "center", fontSize: "13px", color: "#81878C" }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ padding: "12px", textAlign: "center", color: "#FF4D4D", fontSize: "13px" }}>
              {sanitizeInput(error)}
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id || index}
                onClick={() => handleSelectOption(option, index)}
                style={optionStyle(option, index)}
                className="dropdown-option-item"
              >
                {sanitizeInput(String(option.label || option.value || ""))}
              </div>
            ))
          ) : (
            <div style={{ padding: "12px", textAlign: "center", fontSize: "13px", color: "#81878C" }}>
              No options found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchWithFreeText;
